import { AnalyticsEventNameType, DataSource, DataSourceType } from "@coeff/api";
import {
  AuthorizeAmplitudeContent,
  AuthorizeBigQueryContent,
  AuthorizeLinkedInContent,
  AuthorizeLookerContent,
  AuthorizePPressContent,
  AuthorizeSQLContent,
  AuthContextValue,
  AuthProvider as SharedAuthProvider,
  buildPPressMetadata,
  getDataSourceApiErrorMessage,
  getOAuthAuthorizationUrl,
  Graphics,
  Icons,
  Logos,
  PPress,
  PpressContext,
  DataSourceChangeConfirmationContent,
  DataSourceChangeConfirmationLocationState,
  AuthorizeTableauContent,
} from "@coeff/components";
import {
  AuthType,
  DATA_SOURCE_AUTH_TYPE,
  DataSourceTypeDisplayNames,
  isOAuthDataSourceConnection,
} from "@coeff/utils";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey } from "../api";
import { ErrorMessage, Button, CoeLogo, Typography } from "../components";
import { config } from "../config";
import { COLORS } from "../constants";
import { useClientFlagsQuery } from "../hooks/useClientFlagsQuery";
import { AppState } from "../store";
import { useApiContext, useTrack } from "../utils/contexts";

import { ConnectionHelpCard } from "./ConnectionHelpCard";
import { LoadingRoute } from "./LoadingRoute";

declare const __LOCAL_DEVELOPMENT_NGROK_API_URL__: string;

const AUTH_SUCCESS_REDIRECT_PATH = "/oauth_attempt?status=success&from=webapp";

const completePostDataSourceCreation = (config: {
  dataSource: DataSource;
  onComplete: (dataSource: DataSource) => void;
}) => {
  config.onComplete(config.dataSource);
};

const { Content, Footer } = require("antd").Layout;

// Logos for the SQL connectors that render a "Let's reconnect to X" header.
const SQL_REAUTH_LOGOS: Partial<Record<string, React.ReactNode>> = {
  mysql: <Logos.MySQL className="datasourceLogo" />,
  postgres: <Logos.Postgres className="datasourceLogo" />,
  redshift: <Logos.Redshift className="datasourceLogo" />,
  mssql: <Logos.MSSQL className="datasourceLogo" />,
  azure_postgres: <Logos.Azure className="datasourceLogo" />,
  azure_mysql: <Logos.Azure className="datasourceLogo" />,
  azure_sql: <Logos.Azure className="datasourceLogo" />,
};

const WholePageLayout = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${COLORS.black15};
  background: ${COLORS.white};
`;

const HeaderTitle = styled(Typography)`
  margin-left: 12px;
  font-weight: 600;
  font-size: 16px;
  color: ${COLORS.black85};
  flex: 1;
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 0;
  flex: 1;
  background: ${COLORS.white};

  .ant-radio-checked .ant-radio-inner::after {
    left: 50%;
    top: 50%;
  }
`;

const getDataSourceDisplayName = (type?: string) => {
  if (!type) {
    return "Connection";
  }
  return DataSourceTypeDisplayNames[type] || type;
};

/**
 * ReauthPage - Generic reauthorization page for existing connections (/reauth/:dataSourceId)
 * Supports all connectors in webapp except MySQL.
 */
export const ReauthPage: React.FC = () => {
  const { dataSourceId } = useParams<{ dataSourceId: string }>();
  const history = useHistory();
  const { data: clientFlags, isLoading: isLoadingClientFlags } = useClientFlagsQuery();
  const userEmail = useSelector((state: AppState) => state.app.email);
  const { apiClient } = useApiContext();
  const track = useTrack();

  // PPress state
  const [isAuthCompleted, setIsAuthCompleted] = useState(false);
  const [isAuthInProgress, setIsAuthInProgress] = useState(false);

  // Data source change confirmation state
  const [changeConfirmationRequest, setChangeConfirmationRequest] =
    useState<DataSourceChangeConfirmationLocationState>();

  // Fetch dataSource to validate ownership and existence
  const {
    data: dataSource,
    isLoading,
    isError,
  } = useQuery(
    [QueryCacheKey.DATA_SOURCE, dataSourceId],
    () =>
      apiClient
        .getDataSource({ data_source_id: dataSourceId })
        .then(response => response.data.data_source),
    { enabled: Boolean(dataSourceId) }
  );

  const dataSourceUserEmail = dataSource?.user?.email;
  const isOwner = dataSourceUserEmail === userEmail;
  const dataSourceType = dataSource?.data_source_type;

  const handleCancel = () => history.goBack();
  const handleUpserted = () => history.replace(AUTH_SUCCESS_REDIRECT_PATH);

  useEffect(() => {
    if (
      isLoading ||
      isLoadingClientFlags ||
      !clientFlags ||
      isError ||
      !dataSource ||
      !dataSourceType ||
      !isOwner
    ) {
      return;
    }

    track(AnalyticsEventNameType.WebappReauthPageViewed, {
      event_from: "webapp",
      import_data_source: dataSourceType,
      conn_data_source: dataSourceType,
      data_source_id: dataSourceId,
    });
  }, [
    clientFlags,
    dataSource,
    dataSourceId,
    dataSourceType,
    isError,
    isLoading,
    isLoadingClientFlags,
    isOwner,
    track,
  ]);
  // Show loading state while fetching dataSource or client flags
  if (isLoading || isLoadingClientFlags || !clientFlags) {
    return <LoadingRoute />;
  }

  const coeffDevRedirectBaseUrl = __LOCAL_DEVELOPMENT_NGROK_API_URL__ || undefined;

  const authValue: AuthContextValue = {
    apiClient,
    track: (eventName, eventProps) => track(eventName, eventProps),
    eventProps: {
      email: userEmail,
      domain: userEmail?.split("@")[1],
    },
    clientFlags,
    sentryCapture: undefined, // Webapp has its own sentry setup
    router: {
      back: () => history.goBack(),
      push: path => history.push(path),
    },
    // Lets PPress-namespace connectors (e.g. Tableau's embedded username/key-pair form)
    // navigate on success when they create a data source directly via the AuthContext.
    navigateOnDataSourceUpsert: handleUpserted,
    initPostDataSourceCreation: completePostDataSourceCreation,
    dataSource: dataSource,
    renderConnectionHelpCard: ({ dataSourceType: helpDataSourceType }) => (
      <ConnectionHelpCard dataSourceType={helpDataSourceType} />
    ),
    components: {
      PpressContext,
    },
    analyticsContext: {
      fromConnectedSource: false,
      fromOnboarding: false,
    },
    coeffDevRedirectBaseUrl,
    // Utility functions for OAuth and PPress
    getOAuthAuthorizationUrl: (api, params) =>
      getOAuthAuthorizationUrl(api, { ...params, from: "webapp", coeffDevRedirectBaseUrl }),
    buildPPressMetadata,
    getDataSourceApiErrorMessage,
  };

  // Show error if API call fails
  if (isError) {
    return (
      <WholePageLayout>
        <PageHeader>
          <CoeLogo />
        </PageHeader>
        <Container style={{ margin: "20px auto", maxWidth: "500px" }}>
          <ErrorMessage
            noMargin
            title="Connection not accessible"
            message="This connection could not be found or you do not have permission to access it. It may have been deleted or you may need to request access from the connection owner."
          />
        </Container>
      </WholePageLayout>
    );
  }

  // Show error if dataSource doesn't exist or type is missing. Unsupported
  // connector types fall through renderReauthComponent() and render nothing.
  if (!dataSource || !dataSourceType) {
    const displayName = getDataSourceDisplayName(dataSourceType);

    return (
      <WholePageLayout>
        <PageHeader>
          <CoeLogo />
        </PageHeader>
        <Container style={{ margin: "20px auto", maxWidth: "500px" }}>
          <ErrorMessage
            noMargin
            title={`Cannot reconnect ${displayName}`}
            message="This connection could not be found or is not supported in the web app. It may have been deleted or removed. Please check your connected sources and try again."
          />
        </Container>
      </WholePageLayout>
    );
  }

  // Check ownership - only owners can reauth
  if (!isOwner) {
    return (
      <WholePageLayout>
        <PageHeader>
          <CoeLogo />
        </PageHeader>
        <Container style={{ margin: "20px auto", maxWidth: "500px" }}>
          <ErrorMessage
            noMargin
            title={`Cannot reconnect ${getDataSourceDisplayName(dataSourceType)}`}
            message={`This connection is owned by ${
              dataSourceUserEmail ?? "another user"
            }. Only the connection owner can reconnect it. Please contact them to resolve this issue.`}
          />
        </Container>
      </WholePageLayout>
    );
  }

  const renderReauthComponent = () => {
    if (!dataSourceType) {
      return null;
    }

    const commonEventProps = {
      email: userEmail,
      domain: userEmail?.split("@")[1],
    };

    // Get auth type for routing
    const authType = DATA_SOURCE_AUTH_TYPE[dataSourceType];

    const renderPPressContent = () => (
      <AuthorizePPressContent
        isModal
        isAuthCompleted={isAuthCompleted}
        setIsAuthCompleted={setIsAuthCompleted}
        isAuthInProgress={isAuthInProgress}
        setIsAuthInProgress={setIsAuthInProgress}
        dataSourceId={dataSourceId}
        dataSourceType={dataSourceType}
        dataImport={undefined}
        fromConnectedSource={false}
        fromOnboarding={false}
        onChangeConfirmationRequired={confirmationState => {
          setChangeConfirmationRequest(confirmationState);
        }}
        onDataSourceUpserted={handleUpserted}
        initPostDataSourceCreation={completePostDataSourceCreation}
        userEmail={userEmail}
        clientFlags={clientFlags}
        event_props={commonEventProps}
        webappBaseUrl={config.BASE_URL}
        PPress={PPress}
      />
    );

    // SQL connectors render a "Let's reconnect to X" header in edit mode.
    const renderSqlReauthHeader = () => (
      <Graphics
        graphics={
          <div className="graphicsLogo">
            <Icons.ConnectPlugIcon />
            {SQL_REAUTH_LOGOS[dataSourceType] ?? null}
          </div>
        }
        title={`Let's reconnect to ${getDataSourceDisplayName(dataSourceType)}`}
        description={`Authorize Coefficient to reconnect to ${getDataSourceDisplayName(
          dataSourceType
        )}`}
      />
    );

    // Route based on auth type, matching the addon's AUTHORIZE_SCREENS mapping.
    switch (authType) {
      case AuthType.SQL:
        if (dataSourceType === DataSourceType.Bigquery) {
          return (
            <AuthorizeBigQueryContent
              dataSource={dataSource}
              isEditConnectionId={dataSourceId}
              fromConnectedSource={false}
              fromOnboarding={false}
              event_props={commonEventProps}
              onComplete={handleUpserted}
              navigateOnDataSourceUpsert={handleUpserted}
              onCancel={handleCancel}
              renderDataSourceItem={() => (
                <Graphics
                  graphics={
                    <div className="graphicsLogo">
                      <Icons.ConnectPlugIcon />
                      <Logos.BigQuery className="datasourceLogo" />
                    </div>
                  }
                  title="Let's reconnect to BigQuery"
                  description="Authorize Coefficient to reconnect to BigQuery"
                />
              )}
            />
          );
        }
        // databricks, motherduck and arrow_flight_sql are backend-driven and go
        // through the PPress flow (matching the addon's authorizePPress route).
        if (
          dataSourceType === DataSourceType.Databricks ||
          dataSourceType === DataSourceType.Motherduck ||
          dataSourceType === DataSourceType.ArrowFlightSql
        ) {
          return renderPPressContent();
        }
        // mysql, postgres, redshift, mssql use the direct SQL form in edit mode.
        return (
          <AuthorizeSQLContent
            dataSourceType={dataSourceType}
            dataSource={dataSource}
            fromConnectedSource={false}
            fromOnboarding={false}
            onDataSourceUpserted={handleUpserted}
            onCancel={handleCancel}
            initPostDataSourceCreation={completePostDataSourceCreation}
            renderDataSourceItem={renderSqlReauthHeader}
            renderConnectionHelpCard={({ dataSourceType: helpDataSourceType }) => (
              <ConnectionHelpCard dataSourceType={helpDataSourceType} />
            )}
          />
        );

      case AuthType.AMPLITUDE:
        return (
          <AuthorizeAmplitudeContent
            isModal
            dataSourceId={dataSourceId}
            fromConnectedSource={false}
            fromOnboarding={false}
            event_props={commonEventProps}
            onDataSourceUpserted={handleUpserted}
            onCancel={handleCancel}
            initPostDataSourceCreation={completePostDataSourceCreation}
          />
        );

      case AuthType.LOOKER:
        // OAuth Looker uses the PPress flow; API-based Looker uses its own form.
        if (isOAuthDataSourceConnection({ dataSource, dataImport: undefined })) {
          return renderPPressContent();
        }
        // Non-OAuth Looker - uses API credentials
        return (
          <AuthorizeLookerContent
            isModal
            dataSourceId={dataSourceId}
            dataSource={dataSource}
            clientFlags={clientFlags}
            event_props={commonEventProps}
            webappBaseUrl={config.BASE_URL}
            onDataSourceUpserted={handleUpserted}
            onCancel={handleCancel}
            renderDataSourceItem={() => (
              <Graphics
                graphics={
                  <div className="graphicsLogo">
                    <Icons.ConnectPlugIcon />
                    <Logos.Looker className="datasourceLogo" />
                  </div>
                }
                title="Let's reconnect to Looker"
                description="Authorize Coefficient to reconnect to Looker"
              />
            )}
          />
        );

      case AuthType.TABLEAU:
        return (
          <AuthorizeTableauContent
            dataSource={dataSource}
            dataSourceId={dataSourceId}
            event_props={commonEventProps}
            onDataSourceUpserted={handleUpserted}
            onCancel={handleCancel}
            initPostDataSourceCreation={completePostDataSourceCreation}
            renderDataSourceItem={() => (
              <Graphics
                graphics={
                  <div className="graphicsLogo">
                    <Icons.ConnectPlugIcon />
                    <Logos.Tableau className="datasourceLogo" />
                  </div>
                }
                title="Let's reconnect to Tableau"
                description="Authorize Coefficient to reconnect to Tableau"
              />
            )}
          />
        );

      case AuthType.OAUTH:
        // Handles linkedin, google_drive, google_sheets, excel, google_analytics
        if (dataSourceType === DataSourceType.Linkedin) {
          return (
            <AuthorizeLinkedInContent
              dataSource={dataSource}
              isEditConnectionId={dataSourceId}
              browserAgentExtensionUrl={config.BROWSER_AGENT_EXTENSION_URL}
              onComplete={handleUpserted}
            />
          );
        }
        // Other OAUTH connectors not yet supported in webapp
        return null;

      case AuthType.PPRESS:
        // snowflake and mongodb (and all other PPress connectors) render via the
        // backend-driven PPress flow.
        return renderPPressContent();

      default:
        return null;
    }
  };

  const displayName = getDataSourceDisplayName(dataSourceType);

  // Render change confirmation screen if needed
  if (changeConfirmationRequest) {
    return (
      <WholePageLayout>
        <PageHeader>
          <CoeLogo />
          <HeaderTitle>Confirm Account Change</HeaderTitle>
          <Button type="text" onClick={() => setChangeConfirmationRequest(undefined)}>
            Cancel
          </Button>
        </PageHeader>
        <Container>
          <DataSourceChangeConfirmationContent
            style={{ padding: "16px" }}
            existingDataSourceUniqueId={changeConfirmationRequest.existingDataSourceUniqueId}
            newDataSourceUniqueId={changeConfirmationRequest.newDataSourceUniqueId}
            dataSourceType={changeConfirmationRequest.dataSourceType}
            dataSourceId={changeConfirmationRequest.dataSourceId}
            ppressMetadata={changeConfirmationRequest.ppressMetadata}
            credentials={changeConfirmationRequest.credentials}
            additionalDisplayFields={changeConfirmationRequest.additionalDisplayFields}
            existingDataSource={dataSource}
            isFetchingDataSource={isLoading}
            api={apiClient}
            track={(eventName: string, properties?: Record<string, any>) =>
              track(eventName as any, properties)
            }
            onSuccess={() => {
              setChangeConfirmationRequest(undefined);
              handleUpserted();
            }}
            onError={() => {
              console.error("Account change failed. Please try again.");
            }}
            onCancel={() => {
              setChangeConfirmationRequest(undefined);
            }}
          />
        </Container>
      </WholePageLayout>
    );
  }

  // All validations passed - show reauth form
  return (
    <WholePageLayout>
      <PageHeader>
        <CoeLogo />
      </PageHeader>
      <Container>
        <SharedAuthProvider value={authValue}>{renderReauthComponent()}</SharedAuthProvider>
      </Container>
    </WholePageLayout>
  );
};

export default ReauthPage;
